import React, { Suspense, lazy } from 'react';
import PageLoader from './components/PageLoader';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';

import { ThemeProvider } from './contexts/ThemeContext';
import { DesignSessionProvider } from './contexts/DesignSessionContext';
import AnnouncementBar from './components/AnnouncementBar';
import Header from './components/Header';
import FabChat from './components/FabChat';
import Cart from './components/Cart';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import HowItWorks from './pages/HowItWorks';
import HowToDesign from './pages/HowToDesign';
import OurStory from './pages/OurStory';
import NightLamp from './pages/NightLamp';
import Preview from './pages/Preview';

import Checkout from './pages/Checkout';
import ThankYou from './pages/ThankYou';
import Products from './pages/Products';
import ProductPage from './pages/ProductPage';
import Login from './pages/Login';
import ResellerRoute from './components/ResellerRoute';
import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ResellerLayout from './components/ResellerLayout';
import AdminLayout from './components/AdminLayout';
import MakeAdmin from './pages/MakeAdmin';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import OrderTracking from './pages/OrderTracking';
import CustomDesignPage from './pages/CustomDesignPage';

const Builder = lazy(() => import('./Builder'));
const WebsiteInfo = lazy(() => import('./pages/admin/WebsiteInfo'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ResellerDashboard = lazy(() => import('./pages/reseller/ResellerDashboard'));
const ResellerOrders = lazy(() => import('./pages/reseller/ResellerOrders'));
const ResellerOrderDetail = lazy(() => import('./pages/reseller/ResellerOrderDetail'));
const ResellerNewOrder = lazy(() => import('./pages/reseller/ResellerNewOrder'));
const ResellerCustomOrders = lazy(() => import('./pages/reseller/ResellerCustomOrders'));
const ResellerAccount = lazy(() => import('./pages/reseller/ResellerAccount'));
const ResellerPayments = lazy(() => import('./pages/reseller/ResellerPayments'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminCustomOrders = lazy(() => import('./pages/admin/AdminCustomOrders'));
const AdminCustomOrderDetail = lazy(() => import('./pages/admin/AdminCustomOrderDetail'));
const AdminResellers = lazy(() => import('./pages/admin/AdminResellers'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminHolidays = lazy(() => import('./pages/admin/AdminHolidays'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));

const AdminFinancials = lazy(() => import('./pages/admin/AdminFinancials'));
const AdminSmsSettings = lazy(() => import('./pages/admin/AdminSmsSettings'));
const AdminSmsLogs = lazy(() => import('./pages/admin/AdminSmsLogs'));
const AdminSmsCampaigns = lazy(() => import('./pages/admin/AdminSmsCampaigns'));
const AdminWhatsAppSettings = lazy(() => import('./pages/admin/AdminWhatsAppSettings'));
const AdminWhatsAppLogs = lazy(() => import('./pages/admin/AdminWhatsAppLogs'));
const AdminWhatsAppInbox = lazy(() => import('./pages/admin/AdminWhatsAppInbox'));
const AdminWhatsAppConversation = lazy(() => import('./pages/admin/AdminWhatsAppConversation'));
const AdminWhatsAppSavedReplies = lazy(() => import('./pages/admin/AdminWhatsAppSavedReplies'));
const AdminWhatsAppAISettings = lazy(() => import('./pages/admin/AdminWhatsAppAISettings'));
const CustomSMSBroadcast = lazy(() => import('./pages/admin/CustomSMSBroadcast'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductEditor = lazy(() => import('./pages/admin/AdminProductEditor'));
const AdminStock = lazy(() => import('./pages/admin/AdminStock'));
const AdminGoogleMerchant = lazy(() => import('./pages/admin/AdminGoogleMerchant'));
const AdminSetup = lazy(() => import('./pages/admin/AdminSetup'));
const AdminKoombiyoOrders = lazy(() => import('./pages/admin/AdminKoombiyoOrders'));
const OCRTest = lazy(() => import('./pages/OCRTest'));

const AppContent = () => {
  const location = useLocation();

  // Hide header on mobile for builder/design routes and admin/reseller pages
  const isBuilderRoute = location.pathname === '/builder' || location.pathname === '/design';
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isResellerRoute = location.pathname.startsWith('/reseller');
  const isLoginRoute = location.pathname === '/login';
  const isAffiliateRoute = location.pathname.startsWith('/a/');
  const isTrackingRoute = location.pathname.startsWith('/track/');
  const isDesignRoute = location.pathname.includes('/design');
  const shouldHideHeader = isBuilderRoute || isAdminRoute || isResellerRoute || isLoginRoute || isAffiliateRoute || isTrackingRoute || isDesignRoute;

  return (
    <>
      {!shouldHideHeader && <AnnouncementBar />}
      {!shouldHideHeader && <Header />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/how-to-design" element={<HowToDesign />} />
          <Route path="/builder" element={<ProtectedRoute allowedRoles={['admin', 'reseller']}><Builder /></ProtectedRoute>} />
          <Route path="/design" element={<ProtectedRoute allowedRoles={['admin', 'reseller']}><Builder /></ProtectedRoute>} />
          <Route path="/3d-night-lamp" element={<NightLamp />} />

          <Route path="/preview" element={<Preview />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/thankyou" element={<ThankYou />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductPage />} />

          {/* Custom Design Page Route */}
          <Route path="/products/:slug/design" element={<CustomDesignPage />} />

          {/* Order Tracking Route */}
          <Route path="/track/:orderId" element={<OrderTracking />} />

          {/* Blog Routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />



          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/make-admin" element={<MakeAdmin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/ocr-test" element={<OCRTest />} />

          {/* Reseller Portal Routes */}
          <Route path="/reseller" element={<ResellerRoute><ResellerLayout /></ResellerRoute>}>
            <Route path="dashboard" element={<ResellerDashboard />} />
            <Route path="orders" element={<ResellerOrders />} />
            <Route path="custom-orders" element={<ResellerCustomOrders />} />
            <Route path="orders/new" element={<ResellerNewOrder />} />
            <Route path="orders/:orderId" element={<ResellerOrderDetail />} />
            <Route path="payments" element={<ResellerPayments />} />
            <Route path="account" element={<ResellerAccount />} />
          </Route>

          {/* Admin Panel Routes (Authenticated) */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:orderId" element={<AdminOrderDetail />} />
            <Route path="custom-orders" element={<AdminCustomOrders />} />
            <Route path="custom-orders/:orderId" element={<AdminCustomOrderDetail />} />
            <Route path="resellers" element={<AdminResellers />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="holidays" element={<AdminHolidays />} />

            <Route path="website-info" element={<WebsiteInfo />} />

            <Route path="blog" element={<BlogAdmin />} />

            <Route path="financials" element={<AdminFinancials />} />
            <Route path="sms-settings" element={<AdminSmsSettings />} />
            <Route path="sms-logs" element={<AdminSmsLogs />} />
            <Route path="sms-campaigns" element={<AdminSmsCampaigns />} />
            <Route path="custom-sms-broadcast" element={<CustomSMSBroadcast />} />
            <Route path="whatsapp-settings" element={<AdminWhatsAppSettings />} />
            <Route path="whatsapp-ai-settings" element={<AdminWhatsAppAISettings />} />
            <Route path="whatsapp-logs" element={<AdminWhatsAppLogs />} />
            <Route path="whatsapp-inbox" element={<AdminWhatsAppInbox />} />
            <Route path="whatsapp-inbox/:conversationId" element={<AdminWhatsAppConversation />} />
            <Route path="whatsapp-saved-replies" element={<AdminWhatsAppSavedReplies />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductEditor />} />
            <Route path="products/:id" element={<AdminProductEditor />} />
            <Route path="stock" element={<AdminStock />} />
            <Route path="stock" element={<AdminStock />} />
            <Route path="koombiyo-orders" element={<AdminKoombiyoOrders />} />
            <Route path="google-merchant" element={<AdminGoogleMerchant />} />
          </Route>
        </Routes>
      </Suspense>
      {!isResellerRoute && !isLoginRoute && !isTrackingRoute && <Cart />}
      {!isResellerRoute && !isLoginRoute && !isBuilderRoute && !isAdminRoute && !isTrackingRoute && <FabChat />}
    </>
  );
};



// ... existing imports

const App = () => (
  <HelmetProvider>
    <Router>
      <AuthProvider>
        <CartProvider>
          <ThemeProvider>
            <DesignSessionProvider>
              <AppContent />
            </DesignSessionProvider>
          </ThemeProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  </HelmetProvider>
);

export default App;
